/**
 * AuthModal.jsx
 *
 * Sign in / Sign up modal overlay.
 */

import React, { useState } from "react";
import { signIn, signUp, requestPasswordReset } from "../utils/auth";
import { supabase } from "../utils/supabase";

const AuthModal = ({ onAuth, onClose }) => {
  const [mode, setMode] = useState("signin");

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError("");
  setLoading(true);

  try {
    let result;

    if (mode === "signin") {
      result = await signIn(email, password);
    } else {
      result = await signUp(email, password, username);
    }

    if (result.ok) {
      setLoading(false);
      onAuth(result.user); // fire and forget — handleAuth closes modal immediately
    } else {
      setError(result.error);
      setLoading(false);
    }
  } catch (err) {
    console.error("Auth error:", err);
    setError("Something went wrong. Please try again.");
    setLoading(false);
  }
};

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h2 className="auth-title">
            {mode === "signin" ? "SIGN IN" : "SIGN UP"}
          </h2>

          <div className="title-stripe small" />

          <p className="auth-subtitle">
            {mode === "signin"
              ? "Sign in to play head-to-head and view leaderboard"
              : "Create an account to join the leaderboard"}
          </p>
        </div>

        <button
          type="button"
          className="google-signin-btn"
          onClick={() => {
            try {
              // Preserve the current path through the OAuth round trip. With a
              // bare origin, someone signing in from a shared /join/ link came
              // back to the home dash and lost the game (email sign-in never
              // leaves the page, which is why only SSO had the bug).
              const path = window.location.pathname;
              if (path.startsWith("/join/")) {
                // Belt and braces: if Supabase's redirect allow-list rejects a
                // deep path it falls back to the Site URL (the bare origin),
                // so index.js also gets a one-shot marker to finish the trip.
                sessionStorage.setItem(
                  "kyg_pending_join",
                  JSON.stringify({ path, ts: Date.now() })
                );
              }
              supabase.auth.signInWithOAuth({
                provider: "google",
                options: { redirectTo: window.location.origin + path },
              });
            } catch (err) {
              console.error("google sso start error:", err);
              supabase.auth.signInWithOAuth({
                provider: "google",
                options: { redirectTo: window.location.origin },
              });
            }
          }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" style={{ marginRight: 10, flexShrink: 0 }}>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in / Sign up with Google
        </button>

        <div className="auth-divider">
          <span className="auth-divider-line" />
          <span className="auth-divider-text">OR</span>
          <span className="auth-divider-line" />
        </div>

        <div className="auth-tabs">
          <button
            className={`auth-tab ${mode === "signin" ? "active" : ""}`}
            onClick={() => {
              setMode("signin");
              setError("");
            }}
          >
            SIGN IN
          </button>

          <button
            className={`auth-tab ${mode === "signup" ? "active" : ""}`}
            onClick={() => {
              setMode("signup");
              setError("");
            }}
          >
            SIGN UP
          </button>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {/* USERNAME ONLY ON SIGNUP */}
          {mode === "signup" && (
            <div className="auth-field">
              <label className="auth-label">USERNAME (display name)</label>
              <input
                className="auth-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. mattNFL"
                autoComplete="username"
                required
              />
            </div>
          )}

          <div className="auth-field">
            <label className="auth-label">EMAIL</label>
            <input
              className="auth-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              autoComplete="email"
              autoFocus
              required
            />
          </div>

          <div className="auth-field">
            <label className="auth-label">PASSWORD</label>
            <input
              className="auth-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete={
                mode === "signin" ? "current-password" : "new-password"
              }
              required
            />
          </div>

          {/* RESET PASSWORD */}
          <button
            type="button"
            className="auth-forgot"
            onClick={async () => {
              if (!email) {
                setError("Enter your email first.");
                return;
              }

              const result = await requestPasswordReset(email);

              if (!result.ok) {
                setError(result.error);
              } else {
                setError("Password reset email sent.");
              }
            }}
          >
            Forgot Password?
          </button>

          {error && <div className="auth-error">{error}</div>}

          <button
            className="btn btn-primary"
            type="submit"
            disabled={loading}
            style={{ width: "100%", marginTop: "8px" }}
          >
            {loading
              ? "PLEASE WAIT..."
              : mode === "signin"
              ? "SIGN IN"
              : "CREATE ACCOUNT"}
          </button>
        </form>

        <button className="auth-cancel" onClick={onClose}>
          CANCEL
        </button>
      </div>
    </div>
  );
};

export default AuthModal;